import { until, useCoreConfig, useDayjs, usePlayerAccountClient, useUserInfo, useWebauthn } from '#imports'

export function useGtag() {
    const { site, gtag: gtagConfig } = useCoreConfig()
    const { useGetPartnerData } = usePlayerAccountClient()

    const userInfo = useUserInfo()
    const partnerData = useGetPartnerData()
    const dayjs = useDayjs()
    const { isMobileApp } = useWebauthn()

    function gtag(...args: any[]) {
        if (import.meta.server) return

        if (isMobileApp) {
            const command = args[0] // 'event', 'set', 'config' etc.

            // Check if it's an event
            // Only 'event' calls need to have the user_id set
            if (command === 'event') {
                const [event_command, event_type, meta_data = {}] = args

                // Add user_id to meta_data
                if (userInfo.data.value?.id) {
                    meta_data.user_id = String(userInfo.data.value.id)
                }

                if (event_command && event_type) {
                    // Firebase event names must match /^[A-Za-z][A-Za-z0-9_]*$/; the `app_` prefix anchors the leading letter.
                    const event_name_suffix = String(event_type).replace(/\W/g, '_')
                    const eventData = {
                        event_name: `app_${event_name_suffix}`,
                        event_type,
                        meta_data,
                    }

                    window.ReactNativeWebView?.postMessage(JSON.stringify({
                        type: 'track_event',
                        ...eventData, // Add flattened event data, since App has messed up
                        event: eventData
                    }))
                }
            }

            return
        }
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer?.push(arguments)
    }

    async function initGtag() {
        if (import.meta.server) return

        window.dataLayer = window.dataLayer || []

        window.dataLayer.push({
            'gtm.start': dayjs().valueOf(),
            'event': 'gtm.js'
        })

        window.dataLayer.push({
            is_app: isMobileApp
        })

        gtag('consent', 'default', {
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied',
            analytics_storage: 'denied',
            wait_for_update: 500,
        })

        gtag('set', { ads_data_redaction: true })

        // Set dynamic GTM vars
        const vars: Record<string, any> = {
            'site-name': site.name,
        }

        // Wait for queries to be in something else than pending state. If the visitor is not logged in, they will be in idle state, which
        // will allow GTM to complete its setup.
        await until(() => userInfo.status.value !== 'pending' && partnerData.status.value !== 'pending').toBeTruthy()

        if (userInfo.data.value || partnerData.data.value) {
            vars['user-id'] = userInfo.data.value?.id ? String(userInfo.data.value.id) : undefined

            if (partnerData.data.value) {
                vars['partner-id'] = partnerData.data.value.partner_id
                vars['partner-name'] = partnerData.data.value.partner_name
                vars['partner-user-id'] = partnerData.data.value.partner_user_id
            }
            else {
                vars['partner-id'] = null
                vars['partner-name'] = 'Without partner'
            }
        }

        if (userInfo.userJustLoggedIn.value) {
            vars['user-just-logged-in'] = 1
            vars['action'] = 'login-receipt'
        }

        if (userInfo.userJustSignedUp.value) {
            vars['user-just-signed-up'] = 1
            vars['action'] = 'signup-receipt'
        }

        gtag('set', vars)
        // ./ Set dynamic GTM vars

        gtag('js', dayjs().toDate())

        if (gtagConfig?.id) {
            gtag('config', gtagConfig.id)
        }
    }

    return {
        gtag,
        initGtag,
    }
}
